import { MdOutlineAddCircle } from 'react-icons/md';
import {
  ColorGridSize,
  ColorMosaicPreview,
  NewColorID,
  useColorPreview,
  type PaletteColor,
} from './ColorPreview';
import { Palette, PaletteMetrics } from './Palette';
import { Loader } from './Loader.tsx';
import './ColorPreview.css';
import { useWindows } from './Windows.tsx';
import { useEffect, useMemo } from 'react';
import {
  Column,
  ElevatedBorderColor,
  Font,
  IconButton,
  NavigationFilter,
  Row,
  SecondaryButton,
  ThinBorderStyle,
} from './DesignSystem.tsx';
import { useCode } from '@/services/CodeService.tsx';

export function ColorPaletteMosaic() {
  const { state, dispatch, dispatchHelpers } = useColorPreview();

  const { state: windowState, dispatch: windowDispatch } = useWindows();
  const { dispatchHelpers: codeDispatchHelpers } = useCode();

  type ColorPaletteLayout = {
    equalSizedTiles: boolean;
    numberOfColumns: number;
    numberOfGaps: number;
    columnGap: number;
    additionalGapRequiredOntopOfPadding: number;
    columnWidth: number;
  };

  // TODO(LAB-458): Dynamically calculate the layout for the Color Module based on whether it's shown as a fidget module
  const {
    equalSizedTiles,
    numberOfColumns,
    numberOfGaps,
    columnGap,
    additionalGapRequiredOntopOfPadding,
    columnWidth,
  } = useMemo(() => {
    function calculateLayout(): ColorPaletteLayout {
      if (state.isInSlate) {
        return {
          equalSizedTiles: false,
          numberOfColumns: 3,
          numberOfGaps: 2,
          columnGap: 14,
          additionalGapRequiredOntopOfPadding: 14 - PaletteMetrics.padding,
          columnWidth: 144,
        };
      }

      return {
        equalSizedTiles: false,
        numberOfColumns: 3,
        numberOfGaps: 2,
        columnGap: 14,
        additionalGapRequiredOntopOfPadding: 14 - PaletteMetrics.padding,
        columnWidth: 240,
      };
    }
    return calculateLayout();
  }, [state.isInSlate]);

  const isHidden = windowState.windows.colors.isHidden === true;

  const contentWidth =
    columnWidth * numberOfColumns +
    numberOfGaps * columnGap +
    2 * additionalGapRequiredOntopOfPadding;

  // If we don't have a width, calculate it

  const explicitWidth = windowState.windows.colors.size?.width;

  // useEffect(
  //   function setWidthIfUnset() {
  //     if (isHidden) {
  //       return;
  //     }

  //     if (explicitWidth === contentWidth) {
  //       return;
  //     }

  //     windowDispatch({
  //       type: 'setSize',
  //       id: 'colors',
  //       size: { width: contentWidth },
  //     });
  //   },
  //   [windowDispatch, isHidden, contentWidth, explicitWidth],
  // );

  const onAdd = () => {
    dispatchHelpers.addColor({ color: { id: NewColorID(), name: '' } });
  };

  const colors = state.colors;
  const hasEmptyColor = colors.some(({ name }) => name.length === 0);
  const canAddColor = !(
    state.isEmptyPalette ||
    hasEmptyColor ||
    state.isInSlate
  );

  const renderLoadingTiles = () =>
    colors.map((color) => {
      const gridSize = equalSizedTiles
        ? { gridColumn: '1 span', gridRow: '1 span' }
        : ColorGridSize(color.name);
      return (
        <div style={{ display: 'contents' }} key={`loader-${color.id}`}>
          <Loader
            height={{ type: 'fill' }}
            accentColorOverride={color.lightMode}
            style={{ width: '100%', ...gridSize, ...ThinBorderStyle }}
          />
        </div>
      );
    });

  function ColorTiles({ colors }: { colors: Array<PaletteColor> }) {
    return (
      <>
        {colors.map((color) => (
          <div key={color.id} style={{ display: 'contents' }}>
            <ColorMosaicPreview
              color={color}
              equalSizedTiles={equalSizedTiles}
            />
          </div>
        ))}
      </>
    );
  }

  function AddButton() {
    return (
      <Column
        onClick={() => onAdd()}
        style={{
          ...ThinBorderStyle,
          width: '100%',
          gridColumn: '1 span',
          gridRow: '1 span',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <Row>
          <div>
            <MdOutlineAddCircle size={20} color="#174DAC" />
          </div>

          <div style={{ marginInlineStart: 8, color: '#174DAC' }}>
            <Font kind="font-caption">ADD</Font>
          </div>
        </Row>
      </Column>
    );
  }

  const showsDarkModeError = true;
  // TODO(LAB-328): Set `showDarkModeFilter` to true when ready to enable Dark Mode
  const showDarkModeFilter = false;
  const showRegenerateButton = false;

  const showViewSource = !(state.isEmptyPalette || state.isInSlate);

  return (
    <Palette
      title="Colors"
      windowID="colors"
      trailingAction={
        showViewSource && (
          <SecondaryButton
            text={'View Source'}
            onClick={() => {
              codeDispatchHelpers.toggle({ source: 'colors' });
            }}
          />
        )
      }
      footerAction={
        <Row>
          {showDarkModeFilter && (
            <NavigationFilter
              options={['Light Mode', 'Dark Mode']}
              onSelection={(option) => {
                dispatch({
                  type: 'setDarkMode',
                  isDarkMode: option === 'Dark Mode',
                });
              }}
              warningOptions={showsDarkModeError ? ['Dark Mode'] : []}
            />
          )}
          {showRegenerateButton && (
            <IconButton
              type="regenerate"
              text="Regenerate"
              onClick={() => {}}
              style={{ marginInlineEnd: 16, color: ElevatedBorderColor }}
            />
          )}
        </Row>
      }
    >
      <div
        style={{
          marginLeft: additionalGapRequiredOntopOfPadding,
          display: 'grid',
          gridTemplateColumns: `repeat(${numberOfColumns}, ${columnWidth}px)`,
          gridTemplateRows: 'masonry',
          gridAutoRows: `${(180 / 240) * columnWidth}px`,
          gridAutoFlow: 'dense',
          rowGap: columnGap,
          columnGap,
        }}
      >
        {state.isLoading && renderLoadingTiles()}
        {state.isLoading || <ColorTiles colors={colors} />}
        {canAddColor && <AddButton />}
      </div>
    </Palette>
  );
}
